/**
 * Thank You App Component
 *
 * Main application component with routing for Thank You admin interface.
 *
 * @since 1.0.0
 */

import { AdminLayout } from '@redfoxwp/core-components/components/admin-layout';
import { NotificationsContainer } from '@redfoxwp/core-components/components/notifications-container';
import { applyFilters } from '@wordpress/hooks';
import { Route, Switch } from 'wouter';

import { NotificationProvider, SettingsProvider, useNotification } from '../../contexts';
import { Navigation } from '../navigation';
import { PageList } from '../page-list';
import { ProFeatures } from '../pro-features';
import { Settings } from '../settings';
import { SidebarFooter } from '../sidebar-footer';

/**
 * App Content component
 *
 * Internal component that uses the notification context.
 *
 * @since 1.0.0
 *
 * @return JSX element.
 */
const AppContent = (): JSX.Element => {
	const { notifications, removeNotification, addNotification } = useNotification();

	// Apply filter to allow other plugins to add routes
	const additionalRoutes = applyFilters('redfox_thank_you_admin_routes', [], { addNotification }) as JSX.Element[];

	// Apply filter to allow hiding the Pro Features route (hidden when Pro is active)
	const showProFeatures = applyFilters('redfox_thank_you_show_pro_features', true) as boolean;

	return (
		<>
			<AdminLayout
				navigation={<Navigation />}
				sidebarFooter={<SidebarFooter />}
				appName={window?.redfoxThankYouSettings?.pluginName}
				appVersion={window?.redfoxThankYouSettings?.pluginVersion}
			>
				<Switch>
					<Route path='/'>
						<PageList />
					</Route>
					<Route path='/settings/:tab?'>
						<Settings />
					</Route>
					{showProFeatures && (
						<Route path='/pro-features'>
							<ProFeatures />
						</Route>
					)}
					{additionalRoutes}
				</Switch>
			</AdminLayout>
			<NotificationsContainer notifications={notifications} onDismiss={removeNotification} />
		</>
	);
};

/**
 * Thank You App component
 *
 * @since 1.0.0
 *
 * @param props Component props.
 *
 * @return JSX element.
 */
export const App = (): JSX.Element => {
	return (
		<NotificationProvider>
			<SettingsProvider>
				<AppContent />
			</SettingsProvider>
		</NotificationProvider>
	);
};
